import * as React from 'react'
import { useSavedBaseSettings } from '@/modules/settings/react-query/basic/useSavedBasicSettings'
import { useDraftBaseSettings } from '@/modules/settings/hooks/basic/useDraftBaseSettings'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { BasicConfigurationModel } from '@/modules/settings/types/basic'

import { FieldsetInput } from '@/modules/settings/components/FieldsetInput'
import { FieldsetInputAutosize } from '@/modules/settings/components/FieldsetInputAutosize'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { useMutateBaseSettings } from '@/modules/settings/react-query/basic/useMutateBaseSettings'
import { FieldsetSelect } from '../../components/FieldsetSelect'
import { Button } from '@/ui'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import { useMutateGusData } from '../../react-query/basic/useMutateGusData'
import { SettingsSectionWithTabs } from '../../components/SettingsSectionWithTabs'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import {
  settingsDataCompany,
  settingsDataAddress,
  settingsDataOther,
  type BasicSettingField,
} from './fields'
import { usePersistentTab } from '@/modules/shared/stores/tabsStore'
import { RouteComponentProps } from '@/app/navigation/withRouter'

// Stable reference: a fresh `{}` on every render would re-run the draft's reset effect in a loop.
const EMPTY_SETTINGS: Partial<BasicConfigurationModel> = {}

export const BasicSettingsPageContent = ({
  match,
}: RouteComponentProps<{ companyId: string }>) => {
  const { t } = useLocalize()

  const diffLabels = React.useMemo<Record<string, string>>(() => {
    const allFields = [...settingsDataCompany, ...settingsDataAddress, ...settingsDataOther]
    const labels: Record<string, string> = {}
    for (const field of allFields) {
      labels[field.props.name] = t(field.props.label)
    }
    labels.country = t('configuration.basic.country')
    return labels
  }, [t])

  const diffRenderers = React.useMemo<Record<string, (before: unknown, after: unknown) => { before: React.ReactNode; after: React.ReactNode }>>(() => ({
    country: (before, after) => {
      const b = before as { name?: string } | null
      const a = after as { name?: string } | null
      return {
        before: <span className="tw-line-through">{b?.name ?? '—'}</span>,
        after: <>{a?.name ?? '—'}</>,
      }
    },
  }), [])

  const saved = useSavedBaseSettings(match.params.companyId)
  const draft = useDraftBaseSettings(saved.data || EMPTY_SETTINGS)
  const mutation = useMutateBaseSettings(match.params.companyId)
  const gus = useMutateGusData(match.params.companyId)

  const [selectedTab, setSelectedTab] = usePersistentTab('settings-basic', 'a')

  // combined
  const current: BasicConfigurationModel | null =
    saved.isFetched && saved.data
      ? {
          ...saved.data,
          ...draft.data,
        }
      : null
  const wrapConfirm = useWrapConfirm()

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name } = e.target
      draft.updateField(name as keyof BasicConfigurationModel, value)
    },
    [],
  )

  const handleTextareaChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value, name } = e.target
      draft.updateField(
        name as keyof BasicConfigurationModel,
        value.replace(/[\r\n]+/g, ' '),
      )
    },
    [],
  )

  const handleSave = () => {
    // draft.save()
    mutation.mutate(draft.data as Partial<BasicConfigurationModel>)
  }

  const handleReset = React.useCallback(() => {
    draft.reset()
  }, [draft.reset])

  const handleGUSFetch = React.useCallback(async () => {
    const gusRes = await gus.mutateAsync(draft.data)
    // GUS nie zna np. KRS instytucji czy numeru lokalu: brak w odpowiedzi nie może
    // czyścić tego, co firma już ma wpisane.
    draft.updateFields(
      Object.fromEntries(
        Object.entries(gusRes).filter(([, v]) => v != null && v !== ''),
      ) as Partial<BasicConfigurationModel>,
    )
  }, [gus, draft.data, draft.updateFields])

  const { data: countries } = useQueryCountries({
    companyId: match.params.companyId,
  })

  const countriesOptions = React.useMemo(() => {
    return Array.isArray(countries)
      ? countries.map((c) => ({
          label: c.name,
          value: c.id.toString(),
        }))
      : []
  }, [countries])

  const mapper = (field: BasicSettingField) => {
    return (
      <div key={field.props.name} className="tw-h-fit tw-overflow-y-auto">
        {field.type === 'input' && (
          <FieldsetInput
            className="tw-max-w-[428px]"
            {...field.props}
            label={t(field.props.label)}
            placeholder={t(field.props.placeholder)}
            onChange={handleInputChange}
            value={current[field.props.name] || ''}
            name={field.props?.name}
            error={
              draft.errors?.[field.props.name as keyof BasicConfigurationModel]
            }
          />
        )}
        {field.type === 'autosize-input' && (
          <FieldsetInputAutosize
            className="tw-max-w-[428px]"
            {...field.props}
            label={t(field.props.label)}
            placeholder={t(field.props.placeholder)}
            onChange={handleTextareaChange}
            value={current[field.props.name] || ''}
            name={field.props?.name}
            maxRows={3}
            error={
              draft.errors?.[field.props.name as keyof BasicConfigurationModel]
            }
          />
        )}
        {field.type === 'country-select' && (
          <FieldsetSelect
            className="tw-z-30 tw-max-w-[428px]"
            {...field.props}
            label={t(field.props.label)}
            placeholder={t(field.props.placeholder)}
            value={current[field.props.name]?.id?.toString() || ''}
            name={field.props?.name}
            onChange={(e) => {
              const value = e.target.value
              if (!value) {
                draft.updateField<'country'>('country', null)
                return
              }
              const selected = countries?.find((c) => c.id.toString() == value)
              if (selected) {
                draft.updateField<'country'>('country', selected)
              }
            }}
            options={countriesOptions}
          />
        )}
      </div>
    )
  }

  return (
    <SettingsPageWrapper
      isReady={!!current}
      isPending={saved.isFetching || mutation.isPending}
      isError={saved.isError || mutation.isError}
      isDirty={JSON.stringify(draft.data) !== JSON.stringify(saved.data)}
      actions={
        <>
          <Button
            size="sm"
            variant="secondary"
            role="button"
            className="tw-z-10 tw-mb-4 tw-self-end tw-rounded-lg tw-text-content-secondary"
            loading={gus.isPending}
            onClick={wrapConfirm(handleGUSFetch, {
              message: t('configuration-basic.gus-fetch-confirm'),
            })} // @TODO translate
          >
            {t('configuration-basic.fetch-gus-data')}
          </Button>
          <Button
            className="tw-rounded-lg tw-px-4 tw-text-content-secondary"
            variant="secondary"
            size="sm"
            onClick={wrapConfirm(handleReset, {
              message: t('configuration.reset-confirm'),
            })}
          >
            Reset
          </Button>
          <Button
            className="tw-rounded-lg tw-px-4"
            size="sm"
            onClick={wrapConfirm(handleSave, {
              message: t('configuration.save-confirm'),
            })}
          >
            {t('common.save')}
          </Button>
        </>
      }
      errorMsg={t('configuration-basic.load-error')}
      globalErrorMsg={t('configuration-basic.load-error-global')}
      diffBefore={saved.data as Record<string, unknown>}
      diffAfter={draft.data as Record<string, unknown>}
      diffLabels={diffLabels}
      diffRenderers={diffRenderers}
    >
      {current && (
        <SettingsSectionWithTabs
          className="tw-mb-2"
          selected={selectedTab}
          setSelected={setSelectedTab}
        >
          <div
            data-tab-name="a"
            title={t('configuration-basic.tab-company-data')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2"
          >
            {settingsDataCompany.map(mapper)}
          </div>
          <div
            data-tab-name="b"
            title={t('configuration-basic.tab-address')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            {settingsDataAddress.map(mapper)}
          </div>
          <div
            data-tab-name="c"
            title={t('configuration-basic.tab-other')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            {settingsDataOther.map(mapper)}
          </div>
        </SettingsSectionWithTabs>
      )}
    </SettingsPageWrapper>
  )
}
